import { inject, Injectable } from '@angular/core';
import { MagicLinkContext } from '../models/magic-link-context.model';
import { EnvironmentService } from './environment.service';

@Injectable({ providedIn: 'root' })
export class MagicLinkService {
  private readonly environmentService = inject(EnvironmentService);

  async decryptContext(encryptedCtx: string): Promise<MagicLinkContext> {
    const decodedContext = decodeURIComponent(encryptedCtx);
    const encryptedBytes = this.base64ToArrayBuffer(decodedContext);
    const secretKey = this.environmentService.aesSecretKey;
    const iv = this.environmentService.aesIv;

    if (!secretKey || !iv) {
      throw new Error('Magic link AES configuration is missing.');
    }

    const cryptoKey = await globalThis.crypto.subtle.importKey(
      'raw',
      this.textToArrayBuffer(secretKey),
      { name: 'AES-CBC' },
      false,
      ['decrypt']
    );

    const decryptedBuffer = await globalThis.crypto.subtle.decrypt(
      {
        name: 'AES-CBC',
        iv: new Uint8Array(this.textToArrayBuffer(iv)),
      },
      cryptoKey,
      encryptedBytes
    );

    const plaintext = new TextDecoder().decode(decryptedBuffer).replace(/\0+$/, '').trim(); // NOSONAR — no backtracking risk: \0 is a single literal char
    const parts = plaintext.split('_');
    if (parts.length < 6) {
      throw new Error('Magic link context is malformed.');
    }

    const [customerId, campaignIdText, disclosureId, formId, formVersionId, userId] = parts;
    const campaignId = Number.parseInt(campaignIdText, 10);

    if (Number.isNaN(campaignId)) {
      throw new TypeError('Magic link campaign id is invalid.');
    }

    return {
      customerId,
      campaignId,
      disclosureId,
      formId,
      formVersionId,
      userId,
    };
  }

  isValidContext(ctx: MagicLinkContext): boolean {
    return (
      ctx.customerId.trim().length > 0 &&
      ctx.disclosureId.trim().length > 0 &&
      ctx.formId.trim().length > 0 &&
      ctx.formVersionId.trim().length > 0 &&
      ctx.userId.trim().length > 0 &&
      ctx.campaignId > 0
    );
  }

  base64ToArrayBuffer(base64: string): ArrayBuffer {
    const normalized = base64
      .replaceAll('-', '+')
      .replaceAll('_', '/');
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
    const binary = atob(padded);
    const bytes = new Uint8Array(binary.length);

    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.codePointAt(index) ?? 0;
    }

    return bytes.buffer;
  }

  private textToArrayBuffer(text: string): ArrayBuffer {
    return new TextEncoder().encode(text).buffer;
  }
}
